import { useState, useEffect, RefObject } from "react";
import { captureVideoFrame } from "../utils";

interface UseVideoCaptureProps {
  videoRef: RefObject<HTMLVideoElement>;
}

interface UseVideoCaptureReturn {
  videoWidth: number;
  videoHeight: number;
  captureFrame: () => Promise<File>;
}

/**
 * Custom hook to manage video element state and frame capture
 * Handles video dimensions tracking and provides frame capture functionality
 */
export const useVideoCapture = ({ videoRef }: UseVideoCaptureProps): UseVideoCaptureReturn => {
  const [videoWidth, setVideoWidth] = useState(0);
  const [videoHeight, setVideoHeight] = useState(0);

  // Track video dimensions for detection overlay
  useEffect(() => {
    if (videoRef.current) {
      const updateDimensions = () => {
        if (videoRef.current) {
          setVideoWidth(videoRef.current.videoWidth);
          setVideoHeight(videoRef.current.videoHeight);
        }
      };

      updateDimensions();
      videoRef.current.addEventListener('loadedmetadata', updateDimensions);

      return () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener('loadedmetadata', updateDimensions);
        }
      };
    }
  }, [videoRef.current]);

  // Capture frame from video element
  const captureFrame = async (): Promise<File> => {
    if (!videoRef.current) {
      throw new Error("Video element not available");
    }
    return captureVideoFrame(videoRef.current);
  };

  return {
    videoWidth,
    videoHeight,
    captureFrame,
  };
};
